import React, {
  useState, useEffect, useReducer, useImperativeHandle, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import { nanoid } from 'nanoid';
import { useTranslation } from '@/helpers/translate';
import Calendar from './content.js';
import dayjs from '@/helpers/dayjs';
import ClassCalendarStore from './store.js';
import Icon from '@/core/icon.js';
import usePopoverFlip from '@/helpers/usePopoverFlip';
import { useHomeCopy } from '@/components/home/homeCopy';
import {
  CloseIcon, selectClass, selectStyle, removeBtnClass, sectionTitleClass,
} from './lightUi';

const HOUR_MIN = 6;
const HOUR_MAX = 23;
const DEFAULT_SLOT = { start: '09:00', end: '10:00' };
const hourLabel = (h) => `${String(h).padStart(2, '0')}:00`;
const hourRange = (from, to) => Array.from(
  { length: Math.max(0, to - from + 1) },
  (_, i) => hourLabel(from + i),
);
const fill = (str, n) => String(str || '').replace('{n}', n);

function FilterDate({ forwardRef, light }) {
  const [item] = useState({
    id: nanoid(),
    slug: 'dates',
    title: 'search:filter.date',
    flexibility: [
      {
        slug: '1',
        title: '1',
      },
      {
        slug: '3',
        title: '3',
      },
      {
        slug: '7',
        title: '7',
      },
    ],
  });
  const { t, lang } = useTranslation();
  // Variante claire : libellés fr/en/es du dictionnaire de la home (`hero.search.calendar.*`).
  const { tx } = useHomeCopy();
  const cx = (path) => tx(`hero.search.calendar.${path}`);
  const [keySlug] = useState(item.id);
  // eslint-disable-next-line max-len
  const [flexValue, setFlexValue] = useState(0);
  // eslint-disable-next-line max-len
  const [calendarStore, setCalendarStore] = useState(new ClassCalendarStore({ datepicked: [], period: {}, hours: {} }));
  // WEA-163 - le store calendrier est un objet mutable : ce jeton force le re-rendu de la
  // liste des dates quand on y change un creneau.
  const [, setPickTick] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const modalRef = useRef(null);
  const fieldRef = useRef(null);
  const panelRef = useRef(null);
  // Mode light (homepage/popin) : le panneau s'ouvre vers le haut quand le bas
  // de la fenêtre le couperait, en se calant au-dessus du champ dates.
  const flip = usePopoverFlip({
    open: !!(isOpenModal && light), panelRef, anchorRef: fieldRef,
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpenModal(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef]);

  const convertToCalendarStore = (values) => (values || []).reduce((acc, { date, period }) => {
    acc.datepicked.push(dayjs(date.replace(/,000$/, '')).formatAsKey());
    acc.period[dayjs(date.replace(/,000$/, '')).formatAsKey()] = period;
    return acc;
  }, { datepicked: [], period: {} });

  const [currentValue, setCurrentValue] = useReducer((prev, { value }) => {
    let newValue = { ...prev };
    if (value) {
      newValue = { ...newValue, value };
    }
    newValue = { ...newValue, validValue: [...newValue.value] };

    return { ...newValue };
  }, {
    isChange: null,
    value: [],
    validValue: [],
  });

  const getValue = (from) => {
    if (from === 'url') {
      // eslint-disable-next-line max-len, no-unsafe-optional-chaining
      return currentValue?.validValue?.length ? currentValue?.validValue.reduce((acc, {
        date, period, hours, start,
      }) => {
        const newAcc = [...acc];
        const day = dayjs(date.replace(/,000$/, '')).format('YYYYMMDD');
        // Meme format que la page de recherche : `YYYYMMDD-periode[-heures[-heureDebut]]`,
        // c'est elle qui relira cette URL.
        if (!(hours > 0)) newAcc.push(`${day}-${period}`);
        else if (start) newAcc.push(`${day}-${period}-${hours}-${parseInt(start, 10)}`);
        else newAcc.push(`${day}-${period}-${hours}`);
        return newAcc;
      }, []).join('|') + (flexValue ? `*${flexValue}` : '') : null;
    }
    return (currentValue?.validValue?.length && currentValue?.validValue) || null;
  };

  /*
    Le creneau (`start`/`end`) ne part PAS au serveur : `SearchSpaceParamsDValues` ne declare
    que `date`, `period` et `hours`, et une cle inconnue ferait echouer la requete.
  */
  const graphqlValues = () => (getValue() || []).map(({ start, end, ...rest }) => rest);

  const getOutput = () => ({
    keySlug,
    slug: item.slug,
    url: getValue('url'),
    values: getValue(),
    graphql: {
      [item.slug]: {
        values: graphqlValues(),
        ...(flexValue ? { flexibility: [parseInt(flexValue, 10)] } : {}),
      },
    },
    triggers: item.triggers,
  });

  const clearValue = (e) => {
    if (e) e.stopPropagation();
    calendarStore.reset();
    setCurrentValue({ value: [] });
  };

  function isSameDefault() {
    return !currentValue.validValue.length;
  }

  useImperativeHandle(forwardRef, () => ({
    getValue,
    getOutput,
    clearValue,
    setIsOpenModal,
    isSameDefault,
  }), [currentValue, flexValue]);

  function convertCalendarStoreToValidValue() {
    return Object.entries(calendarStore.getPeriod()).reduce((acc, [date, period]) => {
      const newAcc1 = [...acc];
      const hours = calendarStore.hoursOf(date);
      const slot = calendarStore.slotOf(date);
      newAcc1.push({
        date,
        period,
        ...(hours > 0 ? { hours } : {}),
        ...(slot || {}),
      });
      return newAcc1;
    }, []);
  }

  const hasValue = () => currentValue?.validValue?.length;

  const clickValid = () => {
    setCurrentValue({ value: convertCalendarStoreToValidValue() });
    setIsOpenModal(false);
  };

  const selectFlex = (val) => {
    setFlexValue(flexValue === val ? null : val);
  };

  const fmtDate = (key, format) => {
    const out = dayjs(key.replace(/,000$/, '')).locale(lang || 'fr').format(format);
    return out.charAt(0).toUpperCase() + out.slice(1);
  };

  const renderTitle = () => (
    <div className={light ? 'text-left block truncate text-[11px] font-bold tracking-[0.12em] uppercase text-was-muted' : 'text-left block text-[14px] text-purple-4 pt-2 lg:pt-3 pb-0 px-4'} onClick={() => setIsOpenModal(true)}>
      {light ? t(item.title) : `${t(item.title)}(s)`}
    </div>
  );

  /*
    Résumé dans le champ (variante claire) : deux dates maximum en pastilles violettes,
    « +N » au-delà, la flexibilité en pastille pâle, la croix pour tout effacer à droite.
  */
  const renderResumeLight = () => {
    const values = currentValue?.validValue || [];
    if (!values.length) {
      return <div className="text-left text-sm font-medium text-was-ink/50 truncate">{t('core:when')}</div>;
    }
    const shown = values.slice(0, 2);
    const rest = values.length - shown.length;
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        {shown.map((v) => (
          <span className="shrink-0 rounded-md bg-was-violet px-2 py-0.5 text-xs font-medium text-white" key={`filterdate${v.date}`}>
            {fmtDate(v.date, 'DD/MM')}
          </span>
        ))}
        {rest > 0 && <span className="shrink-0 rounded-md bg-was-ink/6 px-1.5 py-0.5 text-xs font-medium text-was-slate">{fill(cx('more'), rest)}</span>}
        {!!flexValue && <span className="shrink-0 rounded-md bg-was-violet/12 px-2 py-0.5 text-xs font-medium text-was-violet">{fill(cx('flexDays'), flexValue)}</span>}
        <button
          type="button"
          className={`ml-auto ${removeBtnClass}`}
          aria-label={cx('clear')}
          onClick={(e) => { clearValue(e); }}
        >
          <CloseIcon />
        </button>
      </div>
    );
  };

  const renderResume = () => {
    if (light) return renderResumeLight();
    if (!(currentValue?.validValue || []).length) {
      return <div className="text-left text-white md:text-[16px]">{t('core:when')}</div>;
    }
    return (
      <div className="flex justify-between">
        <div className="grid overflow-hidden grid-flow-col auto-cols-max gap-x-2 items-center w-full text-black" onClick={() => setIsOpenModal(true)}>
          {(currentValue?.validValue || []).map((v) => <div className="px-2 mt-0.5 text-sm text-white rounded lg:leading-4.5 lg:py-1 bg-purple" key={`filterdate${v.date}`}>{dayjs(v.date.replace(/,000$/, '')).format('DD/M')}</div>)}
          {!flexValue || (
            <p className="flex items-center px-2 mt-0.5 rounded text-purple bg-purple-5 lg:leading-4.5 lg:py-1">
              <Icon name="plusminus" className="mx-1 fill-current" />
              {flexValue}
              j
            </p>
          )}
        </div>
        <button className="w-6" onClick={(e) => { clearValue(e); }}><Icon name="close" className="mx-1 w-full h-3 fill-current text-white" /></button>
      </div>
    );
  };

  /*
    WEA-163 — une ligne par date sélectionnée, reprise du filtre de dates de la recherche.
    Chaque date porte SOIT une période (journée, matin, après-midi) SOIT un créneau horaire,
    jamais les deux : croix à gauche pour retirer la date, bouton « Passer à l'heure » tant
    qu'elle est en période, deux sélecteurs arrivée/départ une fois horaire, et un bouton
    « Journée » nommé — pas une seconde croix, qui se confondrait avec celle de gauche.

    Le créneau est un SOUHAIT : la home ne connaît aucun lieu, donc aucune disponibilité. Il
    voyage dans l'URL (`YYYYMMDD-période-heures-heureDébut`) et c'est la page de recherche,
    puis la fiche du lieu, qui le confrontent aux tranches réellement libres.
  */
  const periodLabel = (period) => {
    if (period === 1) return t('btn:period.morning');
    if (period === 2) return t('btn:period.afternoon');
    return '';
  };

  const refreshFromStore = () => {
    setPickTick(nanoid());
    setCurrentValue({ value: convertCalendarStoreToValidValue() });
  };

  const removeDate = (dateKey) => {
    calendarStore.removeDate(dateKey);
    refreshFromStore();
  };

  const setSlot = (dateKey, slot) => {
    calendarStore.setSlotForDate(dateKey, slot);
    refreshFromStore();
  };

  const backToPeriod = (dateKey) => {
    calendarStore.removeSlotForDate(dateKey);
    refreshFromStore();
  };

  const setPeriodForDate = (dateKey, period) => {
    calendarStore.removeSlotForDate(dateKey);
    calendarStore.setPeriod({ ...calendarStore.getPeriod(), [dateKey]: period });
    refreshFromStore();
  };

  const renderHoursByDate = () => {
    const picked = calendarStore.getDatesPicked();
    if (!picked.length) return null;
    const periodByDate = calendarStore.getPeriod();

    return (
      <div className="mt-3">
        <div className="pb-1 my-1 text-sm text-left text-purple-2">{t('search:byhour.mixed_title')}</div>
        <div className="flex flex-col gap-y-2">
          {picked.map((dateKey) => {
            const slot = calendarStore.slotOf(dateKey);
            const endOptions = slot
              ? hourRange(Number.parseInt(slot.start, 10) + 1, HOUR_MAX)
              : [];

            return (
              <div className="flex gap-2 items-center" key={`homefilterdatehours${dateKey}`}>
                <span
                  className="shrink-0 cursor-pointer"
                  title={t('btn:delete')}
                  onClick={() => removeDate(dateKey)}
                >
                  <Icon className="text-purple-4 fill-current size-3" name="close" />
                </span>
                <span className="flex-1 text-sm text-white">
                  {dayjs(dateKey.replace(/,000$/, '')).format('ddd DD/MM')}
                  {!slot && periodLabel(periodByDate?.[dateKey]) && (
                    <span className="ml-2 text-purple-4">{periodLabel(periodByDate[dateKey])}</span>
                  )}
                </span>

                {slot ? (
                  <div className="flex gap-1 items-center">
                    <select
                      className="p-1 text-sm text-white rounded-md bg-purple-5"
                      value={slot.start}
                      onChange={(ev) => {
                        const nextStart = ev.target.value;
                        const nextEnd = nextStart >= slot.end
                          ? hourLabel(Number.parseInt(nextStart, 10) + 1)
                          : slot.end;
                        setSlot(dateKey, { start: nextStart, end: nextEnd });
                      }}
                      aria-label={t('search:byhour.arrival')}
                    >
                      {hourRange(HOUR_MIN, HOUR_MAX - 1).map((h) => <option key={`hs${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    <select
                      className="p-1 text-sm text-white rounded-md bg-purple-5"
                      value={slot.end}
                      onChange={(ev) => setSlot(dateKey, { start: slot.start, end: ev.target.value })}
                      aria-label={t('search:byhour.departure')}
                    >
                      {endOptions.map((h) => <option key={`he${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    <span
                      className="flex gap-1 items-center px-2 py-1 ml-1 text-xs rounded-md border cursor-pointer shrink-0 text-purple-2 border-purple-4/30 hover:bg-purple-5"
                      title={t('search:byhour.back_to_period')}
                      onClick={() => backToPeriod(dateKey)}
                    >
                      <Icon className="fill-current text-purple-4 size-3" name="dates" />
                      {t('search:byhour.keep_period')}
                    </span>
                  </div>
                ) : (
                  <span
                    className="px-2 py-1 text-xs rounded-md border cursor-pointer text-purple-2 border-purple-4/30 hover:bg-purple-5"
                    onClick={() => setSlot(dateKey, DEFAULT_SLOT)}
                  >
                    {t('search:byhour.to_hourly')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /*
    Variante claire : une ligne par date — le jour en toutes lettres, un sélecteur
    Journée / Matin / Après-midi / Par heure, la croix pour retirer la date ; en horaire,
    une seconde ligne Arrivée / Départ avec la durée.
  */
  const periodLabels = {
    day: cx('period.day'),
    morning: cx('period.morning'),
    afternoon: cx('period.afternoon'),
    hour: cx('period.hour'),
    remove: cx('remove'),
  };

  const renderDatesLight = () => {
    const picked = calendarStore.getDatesPicked();
    if (!picked.length) return null;
    const periodByDate = calendarStore.getPeriod();
    const periodOptions = [
      { value: '0', label: periodLabels.day },
      { value: '1', label: periodLabels.morning },
      { value: '2', label: periodLabels.afternoon },
      { value: 'hour', label: periodLabels.hour },
    ];

    return (
      <div className="mt-4 border-t border-was-ink/8 pt-3">
        <div className={`mb-1 ${sectionTitleClass}`}>{cx('datesTitle')}</div>
        <ul className="divide-y divide-was-ink/6">
          {picked.map((dateKey) => {
            const slot = calendarStore.slotOf(dateKey);
            const hours = calendarStore.hoursOf(dateKey);
            const endOptions = slot
              ? hourRange(Number.parseInt(slot.start, 10) + 1, HOUR_MAX)
              : [];
            const selectValue = slot ? 'hour' : String(periodByDate?.[dateKey] ?? 0);

            return (
              <li className="py-2" key={`homefilterdatehours${dateKey}`}>
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-was-ink">
                    {fmtDate(dateKey, 'ddd D MMM')}
                  </span>
                  <select
                    className={selectClass}
                    style={selectStyle}
                    value={selectValue}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      if (v === 'hour') setSlot(dateKey, DEFAULT_SLOT);
                      else setPeriodForDate(dateKey, Number.parseInt(v, 10));
                    }}
                    aria-label={periodLabels.hour}
                  >
                    {periodOptions.map((o) => <option key={`hp${dateKey}${o.value}`} value={o.value}>{o.label}</option>)}
                  </select>
                  <button
                    type="button"
                    className={removeBtnClass}
                    aria-label={periodLabels.remove}
                    title={periodLabels.remove}
                    onClick={() => removeDate(dateKey)}
                  >
                    <CloseIcon />
                  </button>
                </div>
                {slot && (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-was-muted">
                    <span>{cx('arrival')}</span>
                    <select
                      className={selectClass}
                      style={selectStyle}
                      value={slot.start}
                      onChange={(ev) => {
                        const nextStart = ev.target.value;
                        const nextEnd = nextStart >= slot.end
                          ? hourLabel(Number.parseInt(nextStart, 10) + 1)
                          : slot.end;
                        setSlot(dateKey, { start: nextStart, end: nextEnd });
                      }}
                      aria-label={cx('arrival')}
                    >
                      {hourRange(HOUR_MIN, HOUR_MAX - 1).map((h) => <option key={`hs${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    <span>{cx('departure')}</span>
                    <select
                      className={selectClass}
                      style={selectStyle}
                      value={slot.end}
                      onChange={(ev) => setSlot(dateKey, { start: slot.start, end: ev.target.value })}
                      aria-label={cx('departure')}
                    >
                      {endOptions.map((h) => <option key={`he${dateKey}${h}`} value={h}>{h}</option>)}
                    </select>
                    <span className="ml-auto font-medium text-was-slate">{fill(cx('hoursShort'), hours)}</span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderFlexibility = () => (
    <div className="">
      <div className="pb-1 my-1 text-sm text-left text-purple-2">{t('core:flexibility')}</div>
      <div className="grid grid-cols-3 gap-x-2 text-sm lg:text-base">
        {(item?.flexibility || []).map((f) => (
          <span
            key={`cmpsearchfilterdateflex${f.slug}`}
            className={`text-center justify-center py-1 flex items-center rounded cursor-pointer text-sm ${flexValue !== f.slug ? 'border text-purple-4 border-purple-4/30 hover:bg-purple-5 hover:text-purple' : 'border border-purple bg-purple text-white'}`}
            onClick={() => selectFlex(f.slug)}
          >
            <Icon name="plusminus" className={`fill-current mx-1 ${flexValue !== f.slug || 'text-white'}`} />
            {f.title}
            {' '}
            jour(s)
          </span>
        ))}
      </div>
    </div>
  );

  /*
    Variante claire : un groupe de segments « Dates fixes | ± 1 j | ± 3 j | ± 7 j »,
    l'état est explicite plutôt qu'un bouton à re-cliquer pour désactiver.
  */
  const renderFlexibilityLight = () => {
    const options = [
      { slug: '', label: cx('flexFixed') },
      ...(item?.flexibility || []).map((f) => ({ slug: f.slug, label: fill(cx('flexDays'), f.title) })),
    ];
    return (
      <div className="mt-4 border-t border-was-ink/8 pt-3">
        <div className={`mb-2 ${sectionTitleClass}`}>{cx('flexibility')}</div>
        <div className="grid grid-cols-4 gap-1 rounded-xl bg-was-ink/5 p-1" role="radiogroup" aria-label={cx('flexibility')}>
          {options.map((o) => {
            const selected = (flexValue || '') === o.slug;
            return (
              <button
                key={`cmpsearchfilterdateflex${o.slug || 'fixed'}`}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`rounded-lg px-1 py-1.5 text-[11px] font-medium whitespace-nowrap transition sm:px-2 sm:text-xs ${selected
                  ? 'bg-white text-was-ink shadow-[0_1px_2px_rgba(19,14,27,0.12)]'
                  : 'text-was-slate hover:text-was-ink'}`}
                onClick={() => setFlexValue(o.slug)}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderFooterLight = () => (
    <div className="mt-4 flex items-center justify-between border-t border-was-ink/8 pt-3">
      <button
        type="button"
        className={`text-sm text-was-muted underline underline-offset-2 transition hover:text-was-ink ${hasValue() ? '' : 'invisible'}`}
        onClick={(e) => { clearValue(e); }}
      >
        {cx('clear')}
      </button>
      <button
        type="button"
        className="rounded-[10px] bg-was-ink px-4 py-2 text-sm font-medium text-white transition hover:bg-was-violet active:scale-[0.98]"
        // Le panneau vit DANS le champ de la pilule, dont le clic rouvre le calendrier :
        // on coupe la propagation pour que « Terminé » ferme vraiment.
        onClick={(e) => { e.stopPropagation(); clickValid(); }}
      >
        {cx('done')}
      </button>
    </div>
  );

  const renderContent = () => (
    <>
      <Calendar
        store={calendarStore}
        light={light}
        labels={light ? periodLabels : undefined}
        onSelected={() => {
          setCurrentValue({ value: convertCalendarStoreToValidValue() });
        }}
      />
      {light ? renderDatesLight() : renderHoursByDate()}
      {light ? renderFlexibilityLight() : renderFlexibility()}
      {light
        ? renderFooterLight()
        : <button className="px-3 py-2 mt-3 w-full text-lg text-white bg-red lg:hidden" onClick={() => { setIsOpenModal(false); }}>{t('btn:next')}</button>}
    </>
  );

  const renderModal = () => {
    if (light) {
      /*
        Mobile : bottom sheet fixé en bas de l'écran avec un voile, le panneau ancré
        au champ n'y a pas la place (il prenait la largeur de la seule colonne texte).
        Desktop (lg) : panneau ancré sous le champ, retourné au-dessus si le bas de la
        fenêtre le couperait. Le calcul de `flip` ne vaut que pour l'ancrage desktop.
      */
      const desktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
      const close = (e) => { e.stopPropagation(); setIsOpenModal(false); };
      /*
        Le bottom sheet mobile passe par un portail vers <body> : un ancêtre de la pilule
        garde un `transform` (fondu .was-fade-up), qui ferait positionner le `fixed` par
        rapport à lui et non à l'écran. `modalRef` reste sur la racine du portail pour que
        la fermeture au clic extérieur (contains) fonctionne.
      */
      const sheet = (
        <div className={`lg:relative ${flip ? '' : 'lg:top-2'}`} ref={modalRef}>
          <div className="fixed inset-0 z-40 bg-was-ink/40 lg:hidden" onClick={close} role="presentation" />
          <div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white p-4 pb-6 text-left shadow-[0_-8px_32px_rgba(19,14,27,0.25)] lg:absolute lg:inset-x-auto lg:bottom-auto lg:-left-[43px] lg:z-40 lg:max-h-none lg:w-[360px] lg:overflow-visible lg:rounded-2xl lg:p-5 lg:pb-5 lg:shadow-[0_1px_2px_rgba(19,14,27,0.06),0_8px_16px_-8px_rgba(19,14,27,0.25),0_28px_72px_-12px_rgba(19,14,27,0.55)]"
            ref={panelRef}
            style={flip && desktop ? { bottom: (fieldRef.current?.offsetHeight || 0) + 8 } : undefined}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-was-ink/15 lg:hidden" aria-hidden="true" />
            {renderContent()}
          </div>
        </div>
      );
      if (!desktop && typeof document !== 'undefined') return createPortal(sheet, document.body);
      return sheet;
    }
    return (
      <div className="relative -top-[71px] lg:top-2" ref={modalRef}>
        <div
          className="w-full lg:min-w-[320px] rounded-lg bg-purple-6 absolute z-40 px-4 pb-5 pt-3 shadow-button"
          ref={panelRef}
        >
          <div className="pt-0 text-sm lg:text-base">{renderContent()}</div>
        </div>
      </div>
    );
  };
  return (
    <>
      <div ref={fieldRef} className={light ? 'w-full lg:relative cursor-pointer' : 'mb-2 w-full rounded-lg border lg:relative border-purple/50 shadow-light lg:border-none lg:shadow-none lg:rounded-none lg:mb-0 lg:border-b-0'}>
        {renderTitle()}
        <div className={light ? 'block w-full bg-transparent' : 'bg-transparent lg:relative lg:bottom-[1px] lg:h-12 pt-0 pb-2 lg:pt-2 lg:pb-3 px-4 md:text-md block w-full border-none focus:ring-0'} onClick={() => setIsOpenModal(true)}>
          {renderResume()}
        </div>
      </div>
      {!isOpenModal || renderModal()}
    </>
  );
}

export default FilterDate;
